import { useMemo, useState } from 'react';
import classnames from 'classnames';
import moment from 'moment';
import { useTranslation } from 'hooks';
import { LineChartData } from 'reducers/home';
import Utils from 'helpers/Utils';
import { currencyCodeToSymbol } from 'pages/home/helpers/mapper';
import { OverallChip } from 'pages/home/components/OverallChip/OverallChip';
import { ChartView } from '../ChartView/ChartView';
import './lineChart.scss';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { getWlChartStyleTransformer } from 'helpers/wlResolver';
import Highcharts, { Chart as ChartRef } from 'highcharts';

interface Props {
  data: LineChartData;
  containerHeight?: number
}

export const LineChartView: React.FC<Props> = ({ data, containerHeight }) => {
  const getTranslate = useTranslation();
  const { chartData, period, currency } = data;
  const { increase } = chartData.points;
  const { actual: actualPeriod, previous: previousPeriod } = period;
  const [selectedType, setSelectedType] = useState('current');
  const wlId = useSelector((state: RootState) => state.settings.wlId);
  const styleOptions = wlId ? getWlChartStyleTransformer(wlId) ?? undefined : undefined;

  const { totalActual, totalPrevious } = useMemo(() => {
    const { actual, previous } = chartData.points;

    return {
      totalActual: actual
        .reduce((prev, current) => prev + current, 0)
        .toFixed(2),
      totalPrevious: previous
        .reduce((prev, current) => prev + current, 0)
        .toFixed(2),
    };
  }, [chartData]);

  const chartOptions: Highcharts.Options = useMemo(() => {
    const { actual: actualSeries, previous: previousSeries } = chartData.series;
    const { actual: actualPoints, previous: previousPoints } = chartData.points;
    const isMobile = window.innerWidth < 768;
    const maxLabels = isMobile ? 5 : 11;
    const pointsMaxCount = Math.max(actualPoints.length, previousPoints.length);
    let labelsStep = Math.ceil(pointsMaxCount / maxLabels);
    let labelsUnit = 'day';

    if (pointsMaxCount > 168) {
      // 6 month
      labelsStep = isMobile ? 56 : 28;
      labelsUnit = 'week';
    } else if (pointsMaxCount > 84) {
      // 3 month
      labelsStep = isMobile ? 28 : 14;
      labelsUnit = 'week';
    }

    return {
      chart: {
        spacingTop: 0,
        spacingRight: 0,
        spacingBottom: 0,
        spacingLeft: 0,
        marginTop: 10,
        marginRight: 13,
      },
      title: {
        text: undefined,
      },
      exporting: {
        enabled: false,
      },
      legend: {
        enabled: false,
      },
      credits: {
        enabled: false,
      },
      tooltip: {
        borderRadius: 8,
        borderColor: 'transparent',
        shared: true,
        useHTML: true,
        // highcharts do not support react-components by default
        formatter: function () {
          const { points } = this;

          const markup = points?.map((item) => {
            const { options, series } = item.point;
            const date = options.name;
            const amount = options.y ?? '';
            const type = series.name;

            return `
              <div class="flex justify-between items-center gap-[10px]">
                <div class="${classnames(
              'line-chart__tooltip-date relative pl-[18px] text-text-secondary text-sm-regular',
              {
                'before:border-2 before:border-graph-secondary': type !== selectedType,
              }
            )}">
                  ${moment(date).format('MMM DD')}
                </div>
                <div class="text-text-primary text-m-bold">
                  ${currencyCodeToSymbol[currency]} 
                  ${Utils.getNumberWithSpace(amount)}
                </div>
              </div>
            `;
          });

          return `
            <div class="min-w-[150px] flex flex-col gap-[10px]">
              ${markup?.join('')}
            </div>
          `;
        },
      },
      plotOptions: {
        line: {
          lineWidth: 2,
          cursor: 'pointer',
        },
      },
      xAxis: {
        crosshair: {
          dashStyle: 'ShortDash',
          width: 1,
        },
        categories: Array(Math.max(actualSeries.length, previousSeries.length))
          .fill(null)
          .map((_, idx) => {
            const labelText =
              labelsUnit === 'day'
                ? 'analytics.editForm.params.groupPeriodBy.day.item'
                : 'analytics.editForm.params.groupPeriodBy.weekShort.item';
            const labelNumber =
              labelsUnit === 'day' ? idx + 1 : Math.floor(idx / 7 + 1);
            return `${getTranslate(labelText)} ${labelNumber}`;
          }),
        labels: {
          step: labelsStep,
          formatter: function () {
            const { value } = this;
            return `
              <span class="text-text-tertiary font-body text-sm-regular">
                ${value}
              </span>
            `;
          },
        },
      },
      yAxis: {
        title: undefined,
        labels: {
          formatter: function () {
            const { value } = this;
            return `
              <span class="text-text-tertiary font-body text-sm-regular">
                ${Utils.getNumberWithSuffix(value)}
              </span>
            `;
          },
        },
      },
      series: [
        {
          data: actualPoints.map((item, idx) => [actualSeries[idx], item]),
          type: 'line',
          name: 'current',
          className:
            selectedType === 'current'
              ? 'line-chart__primary-line'
              : 'line-chart__secondary-line',
          zIndex: selectedType === 'current' ? 1 : 0,
          states: {
            hover: {
              halo: null,
              lineWidthPlus: 0,
            },
          },
          marker: {
            enabled: false,
            radius: 3,
            symbol: 'circle',
          },
        },
        {
          data: previousPoints.map((item, idx) => [previousSeries[idx], item]),
          type: 'line',
          name: 'previous',
          className:
            selectedType === 'previous'
              ? 'line-chart__primary-line'
              : 'line-chart__secondary-line',
          zIndex: selectedType === 'previous' ? 1 : 0,
          states: {
            hover: {
              halo: null,
              lineWidthPlus: 0,
            },
          },
          marker: {
            enabled: false,
            radius: 3,
            symbol: 'circle',
          },
        },
      ],
    };
  }, [chartData, selectedType, currency]);

  const chartInfo = [
    {
      type: 'current',
      title: `${currencyCodeToSymbol[currency]} ${Utils.getNumberWithSuffix(
        totalActual
      )}`,
      increase,
      text: getTranslate('getStarted.lineGraph.currentPeriod'),
      period: `(${actualPeriod.from} — ${actualPeriod.to})`,
    },
    {
      type: 'previous',
      title: `${currencyCodeToSymbol[currency]} ${Utils.getNumberWithSuffix(
        totalPrevious
      )}`,
      text: getTranslate('getStarted.lineGraph.previousPeriod'),
      period: `(${previousPeriod.from} — ${previousPeriod.to})`,
    },
  ];

  const options = Highcharts.merge({}, styleOptions, chartOptions);
  return (
    <div className='line-chart'>
      <div className='line-chart__header flex gap-8 mb-8 max-tablet:justify-between max-tablet:gap-4'>
        {chartInfo.map(({ type, title, text, period, increase }) => (
          <div
            key={type}
            className={classnames('line-chart__item', {
              'line-chart__item--active': type === selectedType,
              'select-none cursor-pointer opacity-40': type !== selectedType,
            })}
            role='button'
            onClick={() => setSelectedType(type)}>
            <div className='line-chart__item-top h-[26px] w-full flex items-center gap-2 mb-2'>
              <h3
                className={classnames(
                  'line-chart__title font-heading text-heading-m whitespace-nowrap',
                  {
                    'text-text-secondary leading-[26px] font-heading text-heading-sm max-mobile-s:text-m-bold':
                      type !== selectedType,
                  }
                )}>
                {title}
              </h3>
              {increase ? (
                <div className='line-chart__chip max-mobile-s:hidden'>
                  <OverallChip value={increase} />
                </div>
              ) : null}
            </div>
            <p
              className={classnames(
                'line-chart__period relative pl-[18px] flex gap-1  text-text-secondary text-sm-regular max-mobile-s:text-xs-regular max-mobile-s:flex-col max-mobile-s:gap-[unset] max-mobile-s:leading-[12px]',
                {
                  'text-text-tertiary before:border-graph-secondary': type !== selectedType,
                }
              )}>
              <span>{text}</span>
              <span>{period}</span>
            </p>
          </div>
        ))}
      </div>

      <ChartView
        options={options}
        containerProps={{ style: { height: containerHeight || 148 } }}
      />
    </div>
  );
};
